import {
  Archive,
  ArchiveRestore,
  BellOff,
  Building2,
  MessageCircle,
  Volume2,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  formatMessageRelativeTime,
  type MessageConversationSummary,
} from "@/lib/messages";

export function ConversationRow({
  conversation,
  pending = false,
  onUpdate,
}: {
  conversation: MessageConversationSummary;
  pending?: boolean;
  onUpdate: (
    conversationId: string,
    patch: { muted?: boolean; archived?: boolean },
  ) => void;
}) {
  const rooms = conversation.kind === "rooms";
  const title = rooms
    ? conversation.facility?.name ?? "Facility"
    : conversation.other?.display ?? "Colleague";
  const detail = rooms
    ? conversation.subject ?? "Rooms message"
    : conversation.other
      ? `@${conversation.other.handle}${conversation.other.specialty ? ` · ${conversation.other.specialty}` : ""}`
      : "Direct conversation";

  return (
    <article
      className="pb-messages-conversation-row"
      data-unread={conversation.unread > 0}
    >
      <Link
        className="pb-messages-conversation-row__main"
        to={`/board/messages/${encodeURIComponent(conversation.id)}`}
      >
        <span className="pb-messages-kind" data-kind={conversation.kind} aria-hidden="true">
          {rooms ? <Building2 size={16} /> : <MessageCircle size={16} />}
        </span>
        <span className="pb-messages-conversation-row__copy">
          <span className="pb-messages-conversation-row__title">
            <strong>{title}</strong>
            {!rooms && conversation.other ? (
              <RoleChip role={conversation.other.role} />
            ) : null}
          </span>
          <small>{detail}</small>
          <span className="pb-messages-excerpt">
            {conversation.lastMessage?.mine ? "You: " : ""}
            {conversation.lastMessage?.excerpt ?? "No messages yet."}
          </span>
        </span>
        <span className="pb-messages-conversation-row__meta">
          {conversation.lastMessage ? (
            <time dateTime={conversation.lastMessage.at}>
              {formatMessageRelativeTime(conversation.lastMessage.at)}
            </time>
          ) : null}
          {conversation.unread > 0 ? (
            <span
              className="pb-messages-unread-dot"
              aria-label={`${conversation.unread} unread ${conversation.unread === 1 ? "message" : "messages"}`}
            />
          ) : null}
          {conversation.muted ? <BellOff size={12} aria-label="Muted" /> : null}
        </span>
      </Link>

      <div className="pb-messages-conversation-row__actions">
        <button
          type="button"
          disabled={pending}
          title={conversation.muted ? "Unmute conversation" : "Mute conversation"}
          aria-label={conversation.muted ? "Unmute conversation" : "Mute conversation"}
          onClick={() =>
            onUpdate(conversation.id, { muted: !conversation.muted })
          }
        >
          {conversation.muted ? <Volume2 size={14} /> : <BellOff size={14} />}
        </button>
        <button
          type="button"
          disabled={pending}
          title={conversation.archived ? "Return to inbox" : "Archive conversation"}
          aria-label={conversation.archived ? "Return to inbox" : "Archive conversation"}
          onClick={() =>
            onUpdate(conversation.id, { archived: !conversation.archived })
          }
        >
          {conversation.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
        </button>
      </div>
    </article>
  );
}

export function RoleChip({ role }: { role: string }) {
  const label =
    role === "specialist" ? "Specialist" : role === "gp" ? "GP" : "PulseBoard";
  return (
    <span className="pb-messages-role" data-role={role}>
      {label}
    </span>
  );
}
